/**
 * useOrderFilters Hook
 * Manages filter state for customer orders
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { useState, useCallback, useMemo } from 'react';
import type { CustomerOrderFilters, DateRange } from '../types/order.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// DEFAULT VALUES
// ============================================

/**
 * Default filter state
 */
const DEFAULT_FILTERS: CustomerOrderFilters = {
  searchTerm: '',
  statuses: [],
  dateFrom: undefined,
  dateTo: undefined,
  sortBy: 'orderDate',
  sortDirection: 'desc',
};

// ============================================
// MAIN HOOK
// ============================================

/**
 * Hook for managing order filter state and operations
 *
 * @param initialFilters - Initial filter values
 * @returns Filter state and update functions
 *
 * @example
 * ```tsx
 * const {
 *   filters,
 *   setSearchTerm,
 *   toggleStatus,
 *   setDateRange,
 *   clearFilters,
 *   hasActiveFilters
 * } = useOrderFilters();
 * ```
 */
export function useOrderFilters(initialFilters: CustomerOrderFilters = DEFAULT_FILTERS) {
  const [filters, setFilters] = useState<CustomerOrderFilters>(initialFilters);

  /**
   * Update search term
   */
  const setSearchTerm = useCallback((searchTerm: string) => {
    setFilters(prev => ({ ...prev, searchTerm }));
  }, []);

  /**
   * Toggle a status in the filter array
   */
  const toggleStatus = useCallback((status: OrderStatus) => {
    setFilters(prev => {
      const statuses = prev.statuses || [];
      const hasStatus = statuses.includes(status);

      return {
        ...prev,
        statuses: hasStatus
          ? statuses.filter(s => s !== status)
          : [...statuses, status],
      };
    });
  }, []);

  /**
   * Set all statuses
   */
  const setStatuses = useCallback((statuses: OrderStatus[]) => {
    setFilters(prev => ({ ...prev, statuses }));
  }, []);

  /**
   * Set date range
   */
  const setDateRange = useCallback((dateFrom?: string, dateTo?: string) => {
    setFilters(prev => ({ ...prev, dateFrom, dateTo }));
  }, []);

  /**
   * Set date range from DateRange object
   */
  const setDateRangeFromObject = useCallback((range: DateRange) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: range.from || undefined,
      dateTo: range.to || undefined,
    }));
  }, []);

  /**
   * Set sort configuration
   */
  const setSort = useCallback((
    sortBy: CustomerOrderFilters['sortBy'],
    sortDirection: CustomerOrderFilters['sortDirection']
  ) => {
    setFilters(prev => ({ ...prev, sortBy, sortDirection }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchTerm !== '' ||
      (filters.statuses && filters.statuses.length > 0) ||
      filters.dateFrom !== undefined ||
      filters.dateTo !== undefined
    );
  }, [filters]);

  /**
   * Get count of active filters
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.searchTerm) count++;
    if (filters.statuses && filters.statuses.length > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    return count;
  }, [filters]);

  return {
    filters,
    setSearchTerm,
    toggleStatus,
    setStatuses,
    setDateRange,
    setDateRangeFromObject,
    setSort,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}

export default useOrderFilters;
